import moment from 'moment-timezone';

export const formatInterviewerSchedule = (data) => {
  const { events, users_info: userInfo } = data || {};
  let overallEvents = [];

  userInfo?.forEach((user, index) => {
    const generatedEvents = events?.[user.id]?.map((event) => {
      return {
        title: event.subject,
        start: event.start_time,
        end: event.end_time,
        classNames: `interviewer-event ${
          moment(event.end_time).isBefore(moment()) && 'past'
        } user-${index + 1}`,
        overlap: true,
        editable: false,
        extendedProps: { ...user, type: 'interviewer-event' },
      };
    });

    overallEvents = [...overallEvents, ...generatedEvents];
  });

  return overallEvents;
};

export const formatBackgroundEvent = (interviewerSchedule) => {
  const bgEvents = [];
  interviewerSchedule?.interviewers?.forEach((schedule) => {
    schedule?.interviewer_weekly_available_intervals.forEach((interval) => {
      bgEvents.push({
        title: '',
        type: 'un-available-slots',
        start: interval.start,
        end: interval.end,
        classNames: `bg bg-${schedule.interviewer_id}`,
        overlap: true,
        editable: false,
        groupId: 'lesson-available',
        display: 'inverse-background',
        color: '#e8e8e8',
      });
    });
  });

  return bgEvents;
};

export const combineTimeAndDate = (time, dateWithoutTime, timeZone) => {
  const [actualTime, meridian] = time?.split(' ') || [];
  const [hourStr, minutes] = actualTime?.split(':') || [];
  let hours = hourStr;

  const date = moment(dateWithoutTime).format('YYYY-MM-DD');

  if (meridian === 'AM' && hours === '12') {
    hours = 0;
  } else if (meridian === 'PM' && hours !== '12') {
    hours = Number(hours) + 12;
  }

  return moment
    .tz(`${date} ${hours}:${minutes}`, 'YYYY-MM-DD HH:mm', timeZone)
    .format('YYYY-MM-DDTHH:mm:ssZ');
};

const formatDateToTime = (DateDate) => {
  const momentDate = moment.parseZone(DateDate);

  const hours = momentDate.hour();
  const minutes = momentDate.minutes();

  let formattedHours = hours % 12;
  formattedHours = formattedHours ? formattedHours : 12;

  const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

  const period = hours < 12 ? 'AM' : 'PM';

  const timeString =
    String(formattedHours).padStart(2, 0) +
    ':' +
    formattedMinutes +
    ' ' +
    period;

  return timeString;
};

export const formatEventToForm = ({ start, end }) => {
  const date = new Date(start.slice(0, 19));
  const endDate = new Date(end.slice(0, 19));
  date.setHours(0, 0, 0, 0);
  return {
    activeDate: date,
    activeStart: {
      label: formatDateToTime(start),
      value: formatDateToTime(start),
    },
    activeEnd: {
      label: formatDateToTime(end),
      value: formatDateToTime(end),
    },
    activeEndDate: endDate,
  };
};

export const configureCreateInterviewData = ({
  values,
  jobId,
  stageId,
  actionId,
  candidateId,
  applicationId,
  timezone,
  candidateTemplate,
  interviewerTemplate,
  events,
  variant,
  schedule_type_modified,
  c_scheduled_type,
  schedule_slots_modified,
  scorecard_config,
  scorecard_feedback_description = '',
  scorecard_rating_description = '',
}) => {
  let time_slots;
  if (events?.length) {
    time_slots = events?.map((event) => ({
      start_at: event?.start,
      end_at: event?.end,
    }));
  }

  const interviewDetails = {
    name: values?.interviewName,
    location: values?.location,
    ats_office_id: values?.office,
    ats_application_id: applicationId,
    participant_ids: values?.participant_ids?.map((data) => data.id),
    // * interview_pool_id: 1, //required if interview_type is one_on_one and interview_pool is true
    interviewer_template_id: interviewerTemplate?.id,
    interviewer_description: values?.interviewerEmailEditor,
    candidate_template_id: candidateTemplate?.id,
    candidate_description: values?.candidateEmailEditor,
    timezone,
    duration: values?.duration,
    ats_job_id: jobId,
    ats_job_stage_id: stageId,
    ats_job_stage_action_id: actionId,
    scorecard_config,
    scorecard_feedback_description,
    scorecard_rating_description,
    note_taker_enabled: values?.enable_note_taker,
    note_taker_mandatory: values?.note_taker_mandatory,
    schedule_type_modified: schedule_type_modified,
    schedule_slots_modified: schedule_slots_modified,
    ...(variant === 'manual'
      ? {
          start_time: combineTimeAndDate(
            values?.starts,
            values?.date,
            timezone
          ),
          end_time: combineTimeAndDate(values?.ends, values?.endDate, timezone),
          candidate_id: candidateId,
          kind: values?.interview_type,
        }
      : {
          time_slots,
          schedule_type:
            values?.schedule_type === 'Selected Option'
              ? c_scheduled_type
              : values?.schedule_type,
          contact_id: candidateId,
          interview_kind: values?.interview_type,
        }),
  };

  return interviewDetails;
};

export const addMinutes = (date, minutes, timezone) =>
  moment
    .tz(date, timezone)
    .add(minutes, 'minutes')
    .format('YYYY-MM-DDTHH:mm:ssZ');

export const addMinuteWithDuration = (timeStr, duration, date) => {
  const time = moment(timeStr, 'hh:mm A');
  const combinedDateTime =
    moment(date).format('YYYY-MM-DD') + ' ' + time.format('HH:mm');
  const result = moment(combinedDateTime, 'YYYY-MM-DD HH:mm');
  result.add(duration, 'minutes');

  return { time: result.format('hh:mm A'), date: result.toDate() };
};

export const convertToScrollTime = (timeString) =>
  moment(timeString, 'hh:mm A').format('HH:mm:ss');

export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) {
    return true;
  }
  const dateTime1 = moment(date1);
  const dateTime2 = moment(date2);

  return date1 ? dateTime1.isSame(dateTime2, 'day') : true;
};

export const isSameTime = (date1, date2) => {
  if (!date1 || !date2) {
    return true;
  }
  const dateTime1 = moment(date1);
  const dateTime2 = moment(date2);

  return date1 ? dateTime1.isSame(dateTime2, 'minute') : true;
};

export const isToday = (date) => {
  const targetDate = moment(date);

  return targetDate.isSame(moment(), 'day');
};

export const getNext15MinuteMark = (timezone) => {
  const now = moment.tz(timezone);
  const next15Mins = moment.tz(
    Math.ceil(+now / (15 * 60 * 1000)) * (15 * 60 * 1000),
    timezone
  );

  return next15Mins.format('hh:mm A');
};

export const isGreater = (time1, time2) => {
  const t1 = moment(time1, 'h:mm A');
  const t2 = moment(time2, 'h:mm A');

  return t1.isSameOrAfter(t2);
};

export const constructStartAndEndTime = (start, end) => {
  const startTime = moment(start);
  const endTime = moment(end);

  return `${startTime.format('hh:mm A')} - ${endTime.format('hh:mm A')}`;
};
